import React, { useState, useEffect, useCallback, useRef } from 'react';
import { IconDatabase, IconRefresh, IconSearch, IconTrash, IconStar, IconStarFilled } from '@tabler/icons-react';
import api from '../api/api';
import { ScreeningDetailModal } from './MarketAnalysisPage';

const fmt = (v) => (v == null ? '-' : Number(v).toLocaleString());
const fmtWon = (v) => (v == null ? '-' : fmt(v) + '원');
const fmtCap = (v) => {
  if (v == null) return '-';
  const n = Number(v);
  return n >= 10000 ? (n / 10000).toFixed(1) + '조' : fmt(n) + '억';
};
const fmtWonCap = (v) => (v == null ? '-' : fmtCap(Number(v) / 100_000_000));
const fmtDec2 = (v) => (v == null ? '-' : Number(v).toFixed(2));
const fmtPct = (v) => (v == null ? '-' : Number(v).toFixed(2) + '%');
const fmtDt = (v) => (v ? String(v).replace('T', ' ').slice(0, 16) : '-');
const getKSTDate = () => {
  const now = new Date();
  const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  return kst.toISOString().slice(0, 10);
};

/* 다크 테마에서도 잘 보이는 입력 스타일 (시장분석 페이지와 동일 톤) */
const inputStyle = { background: '#1e2a44', color: '#f0f3fa', border: '1px solid #3d5578', colorScheme: 'dark' };

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 480);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 480);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

/* ── API 호출 함수 ──────────────────────────────── */
const dataApi = {
  getLatestDate: () => api.get('/krx/data/latest-date').then(r => r.data),
  getStock: (params) => api.get('/krx/data/stock', { params }).then(r => r.data),
  getInvestor: (params) => api.get('/krx/data/investor', { params }).then(r => r.data),
  getValuation: (params) => api.get('/krx/data/valuation', { params }).then(r => r.data),
  getShortSelling: (params) => api.get('/krx/data/short-selling', { params }).then(r => r.data),
  getScreeningDetail: (date, market, stockCode) => api.get('/krx/market-analysis/screening/detail', { params: { date, market, stockCode } }).then(r => r.data),
  collectKrxStockDaily: (date) => api.post('/krx/stock-daily/collect', null, { params: { date } }).then(r => r.data),
  collectKrxEtf: (date) => api.post('/krx/etf/collect', null, { params: { date } }).then(r => r.data),
  collectKrxInvestorTrading: (date) => api.post('/krx/investor-trading/collect', null, { params: { date } }).then(r => r.data),
  collectKrxStockValuation: (date) => api.post('/krx/stock-valuation/collect', null, { params: { date } }).then(r => r.data),
  collectKrxShortSelling: (date) => api.post('/krx/short-selling/collect', null, { params: { date } }).then(r => r.data),
  deleteByDate: (date) => api.delete('/krx/data', { params: { date } }).then(r => r.data),
  getKrxDataStatus: (startDate, endDate) => api.get('/krx/data-status', { params: { startDate, endDate } }).then(r => r.data),
  getFavorites: () => api.get('/krx/data/favorites').then(r => r.data),
  addFavorite: (stockCode) => api.post('/krx/data/favorites', null, { params: { stockCode } }).then(r => r.data),
  removeFavorite: (stockCode) => api.delete('/krx/data/favorites', { params: { stockCode } }).then(r => r.data),
  getEtf: (params) => api.get('/krx/etf', { params }).then(r => r.data),
  getEtfDetail: (etfCode, date) => api.get(`/krx/etf/${etfCode}`, { params: { date } }).then(r => r.data),
};

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const fmtYmd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/* 날짜 검색 필터용 datepicker — [시장분석] 상단 달력과 동일하게 krx_stock_daily/krx_investor_trading_daily
   /krx_stock_valuation_daily/krx_short_selling_daily/krx_etf_daily 5개 테이블의 데이터 존재여부를 날짜 셀에 점으로 표시 */
function DataStatusDatePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => new Date());
  const [statusMap, setStatusMap] = useState({});
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  /* 팝업을 열 때마다 현재 선택된 값(value)의 월로 캘린더를 맞춘다 (value가 아직 없으면 무시) */
  useEffect(() => {
    if (!open) return;
    const [y, m] = value.split('-').map(Number);
    if (!Number.isNaN(y) && !Number.isNaN(m)) {
      setViewDate(new Date(y, m - 1, 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const monthStart = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    const monthEnd = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0);
    let alive = true;
    dataApi.getKrxDataStatus(fmtYmd(monthStart), fmtYmd(monthEnd))
      .then(res => { if (alive) setStatusMap(res || {}); })
      .catch(() => { if (alive) setStatusMap({}); });
    return () => { alive = false; };
  }, [open, viewDate]);

  const monthStart = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const daysInMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate();
  const startWeekday = monthStart.getDay();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const changeMonth = (delta) => setViewDate(v => new Date(v.getFullYear(), v.getMonth() + delta, 1));

  const selectDay = (d) => {
    onChange(fmtYmd(new Date(viewDate.getFullYear(), viewDate.getMonth(), d)));
    setOpen(false);
  };

  const dotColor = (count) => {
    if (count === 5) return '#2fb344';
    if (count > 0) return '#f59e0b';
    return null;
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="form-control form-control-sm"
        style={{ ...inputStyle, width: 150, textAlign: 'left', cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}
      >
        {value}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '110%', left: 0, zIndex: 50,
          background: '#1e2a44', border: '1px solid #3d5578', borderRadius: 8,
          padding: 10, width: 240, boxShadow: '0 8px 24px rgba(0,0,0,.4)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <button type="button" onClick={() => changeMonth(-1)}
              style={{ background: 'none', border: 'none', color: '#f0f3fa', cursor: 'pointer', fontSize: 14, padding: '2px 8px' }}>‹</button>
            <div style={{ color: '#f0f3fa', fontSize: 13, fontWeight: 700 }}>
              {viewDate.getFullYear()}년 {viewDate.getMonth() + 1}월
            </div>
            <button type="button" onClick={() => changeMonth(1)}
              style={{ background: 'none', border: 'none', color: '#f0f3fa', cursor: 'pointer', fontSize: 14, padding: '2px 8px' }}>›</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 2 }}>
            {WEEKDAYS.map(w => (
              <div key={w} style={{ textAlign: 'center', color: '#7c8db0', fontSize: 10, padding: '2px 0' }}>{w}</div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {cells.map((d, i) => {
              if (d == null) return <div key={i} />;
              const ymd = fmtYmd(new Date(viewDate.getFullYear(), viewDate.getMonth(), d));
              const isSelected = ymd === value;
              const color = dotColor(statusMap[ymd] || 0);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => selectDay(d)}
                  style={{
                    position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                    padding: '4px 0', background: isSelected ? '#206bc4' : 'transparent',
                    border: 'none', borderRadius: 4, cursor: 'pointer',
                    color: isSelected ? '#fff' : '#e6e6e6', fontSize: 11,
                  }}
                >
                  <span>{d}</span>
                  <span style={{ width: 4, height: 4, borderRadius: '50%', background: color || 'transparent' }} />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const TABS = [
  { key: 'stock', label: '시세/시총' },
  { key: 'etf', label: 'ETF' },
  { key: 'investor', label: '투자자 수급' },
  { key: 'valuation', label: '밸류에이션' },
  { key: 'short', label: '공매도' },
];

const TAB_API = { stock: dataApi.getStock, investor: dataApi.getInvestor, valuation: dataApi.getValuation, short: dataApi.getShortSelling, etf: dataApi.getEtf };

/* ── KRX 데이터 조회 화면 (자산 > KRX 데이터) ────────────────────────────── */
export default function KrxDataPage() {
  const [date, setDate] = useState('');
  const [market, setMarket] = useState('ALL');
  const [keywordInput, setKeywordInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [investorType, setInvestorType] = useState('ALL');
  const [activeTab, setActiveTab] = useState('stock');
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [krxLoading, setKrxLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [detailStock, setDetailStock] = useState(null);
  const [favoriteStockCodes, setFavoriteStockCodes] = useState(() => new Set());
  /* 즐겨찾기만 보기 on/off는 다음 접속 시에도 유지되도록 로컬스토리지에 저장한다 */
  const [favoriteOnly, setFavoriteOnly] = useState(() => localStorage.getItem('krxData.favoriteOnly') === 'true');
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    localStorage.setItem('krxData.favoriteOnly', String(favoriteOnly));
  }, [favoriteOnly]);

  useEffect(() => {
    dataApi.getLatestDate().then(res => setDate(res.date || getKSTDate())).catch(() => setDate(getKSTDate()));
  }, []);

  const loadFavorites = useCallback(() => {
    return dataApi.getFavorites()
      .then(list => setFavoriteStockCodes(new Set(list || [])))
      .catch(() => setFavoriteStockCodes(new Set()));
  }, []);

  useEffect(() => { loadFavorites(); }, [loadFavorites]);

  const search = useCallback(() => {
    if (!date) return;
    setLoading(true);
    const params = { date, page, size: pageSize };
    if (activeTab !== 'etf' && market && market !== 'ALL') params.market = market;
    const kw = keyword.trim();
    if (kw) {
      if (activeTab === 'etf') {
        if (/^\d{5,6}$/.test(kw)) params.etfCode = kw; else params.etfName = kw;
      } else {
        if (/^\d{5,6}$/.test(kw)) params.stockCode = kw; else params.stockName = kw;
      }
    }
    if (activeTab === 'investor' && investorType !== 'ALL') params.investorType = investorType;
    if (sortBy) { params.sortBy = sortBy; params.sortDir = sortDir; }
    if (favoriteOnly) params.favoriteOnly = true;

    TAB_API[activeTab](params)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [date, page, pageSize, market, keyword, activeTab, investorType, sortBy, sortDir, favoriteOnly]);

  useEffect(() => { search(); }, [search]);
  useEffect(() => { setPage(0); }, [activeTab, date, market, keyword, investorType, pageSize, sortBy, sortDir, favoriteOnly]);
  useEffect(() => { setSortBy(null); setSortDir('desc'); }, [activeTab]);

  const handleSearchClick = () => setKeyword(keywordInput);

  /* 종목 즐겨찾기 토글 (계정별, stock_code 기준) */
  const toggleStockFavorite = async (stockCode) => {
    if (!stockCode) return;
    const isFav = favoriteStockCodes.has(stockCode);
    try {
      if (isFav) {
        await dataApi.removeFavorite(stockCode);
      } else {
        await dataApi.addFavorite(stockCode);
      }
      setFavoriteStockCodes(prev => {
        const next = new Set(prev);
        if (isFav) next.delete(stockCode); else next.add(stockCode);
        return next;
      });
    } catch (e) {
      alert(`즐겨찾기 처리 실패: ${e.response?.data?.message || e.message}`);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  /* [KRX데이터수집] 버튼 — 시세 → ETF → 투자자별 순매수 → PER/PBR/배당수익률 → 공매도 순으로 순차 수집 */
  const handleKrxFetch = async () => {
    if (!date) return;
    setKrxLoading(true);
    try {
      const savedCounts = await dataApi.collectKrxStockDaily(date);
      if (!savedCounts.KOSPI && !savedCounts.KOSDAQ) {
        alert(`${date}는 영업일이 아닌 것으로 보입니다 (시세 데이터 없음). 수집을 종료합니다.`);
        return;
      }
      const etfResult = await dataApi.collectKrxEtf(date);
      const investorCounts = await dataApi.collectKrxInvestorTrading(date);
      const valuationCounts = await dataApi.collectKrxStockValuation(date);
      const shortSellingCounts = await dataApi.collectKrxShortSelling(date);

      const etfSummary = etfResult.status === 'NO_TRADING_DAY'
        ? '휴장일로 판단되어 저장하지 않음'
        : `${etfResult.etfCount}종목`;

      alert(
        `KRX 저장 완료 (${date})\n`
        + `[시세] KOSPI ${savedCounts.KOSPI}종목 / KOSDAQ ${savedCounts.KOSDAQ}종목\n`
        + `[ETF] ${etfSummary}\n`
        + `[투자자별 순매수] ${Object.entries(investorCounts).map(([k, v]) => `${k} ${v}건`).join(', ')}\n`
        + `[PER/PBR/배당수익률] ${Object.entries(valuationCounts).map(([k, v]) => `${k} ${v}종목`).join(', ')}\n`
        + `[공매도] ${Object.entries(shortSellingCounts).map(([k, v]) => `${k} ${v}건`).join(', ')}`
      );
      search();
    } catch (e) {
      alert(`KRX 저장 실패: ${e.response?.data?.message || e.message}`);
    } finally {
      setKrxLoading(false);
    }
  };

  /* [yyyy-mm-dd 삭제] 버튼 — 선택된 날짜의 4개 KRX 원본 테이블 데이터를 모두 삭제 */
  const handleDeleteDate = async () => {
    if (!date) return;
    if (!window.confirm(`${date} 데이터를 삭제하시겠습니까?\n(시세/시총, 투자자 수급, 밸류에이션, 공매도 4개 테이블 전체)\n이 작업은 되돌릴 수 없습니다.`)) return;
    setDeleteLoading(true);
    try {
      const counts = await dataApi.deleteByDate(date);
      alert(
        `${date} 데이터 삭제 완료\n`
        + `[시세/시총] ${counts.stock}건\n`
        + `[투자자 수급] ${counts.investor}건\n`
        + `[밸류에이션] ${counts.valuation}건\n`
        + `[공매도] ${counts.shortSelling}건`
      );
      search();
    } catch (e) {
      alert(`삭제 실패: ${e.response?.data?.message || e.message}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  const openDetail = (row) => {
    if (activeTab === 'etf') {
      setDetailStock({ date, isEtf: true, stockCode: row.etfCode, stockName: row.etfName });
    } else {
      setDetailStock({ date, market: row.market, stockCode: row.stockCode, stockName: row.stockName });
    }
  };

  return (
    <div className="container-xl py-4" style={{ color: '#e6e6e6' }}>
      <div className="page-header mb-3">
        <div className="row align-items-center">
          <div className="col">
            <div className="page-pretitle">자산</div>
            <h2 className="page-title d-flex align-items-center gap-2">
              <IconDatabase size={24} />
              KRX 데이터
            </h2>
          </div>
          <div className="col-auto d-flex gap-2 align-items-center flex-wrap">
            <button className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
              onClick={handleKrxFetch} disabled={krxLoading}>
              <IconRefresh size={14} className={krxLoading ? 'rotating' : ''} />
              {krxLoading ? 'KRX 저장 중...' : 'KRX데이터수집'}
            </button>
            <button className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
              onClick={handleDeleteDate} disabled={!date || deleteLoading}>
              <IconTrash size={14} />
              {deleteLoading ? '삭제 중...' : `${date || ''} 삭제`}
            </button>
          </div>
        </div>
      </div>

      {/* 공통 검색 조건 */}
      <div className="d-flex gap-2 align-items-center flex-wrap mb-3" style={{ background: '#152238', border: '1px solid #2a3a5a', borderRadius: 8, padding: 12 }}>
        <button
          type="button"
          className="btn btn-sm btn-icon"
          style={{ background: favoriteOnly ? 'rgba(245,159,0,.15)' : 'transparent', border: '1px solid #3d5578', color: favoriteOnly ? '#f59f00' : '#7c8db0' }}
          onClick={() => setFavoriteOnly(v => !v)}
          title={favoriteOnly ? '즐겨찾기만 보기 해제' : '즐겨찾기 종목만 보기'}
        >
          {favoriteOnly ? <IconStarFilled size={16} /> : <IconStar size={16} />}
        </button>
        <DataStatusDatePicker value={date} onChange={setDate} />
        {activeTab !== 'etf' && (
          <select className="form-select form-select-sm" style={{ ...inputStyle, width: 110 }}
            value={market} onChange={e => setMarket(e.target.value)}>
            <option value="ALL">전체</option>
            <option value="KOSPI">KOSPI</option>
            <option value="KOSDAQ">KOSDAQ</option>
          </select>
        )}
        {activeTab === 'investor' && (
          <select className="form-select form-select-sm" style={{ ...inputStyle, width: 100 }}
            value={investorType} onChange={e => setInvestorType(e.target.value)}>
            <option value="ALL">투자자 전체</option>
            <option value="INDIVIDUAL">개인</option>
            <option value="FOREIGNER">외국인</option>
            <option value="INSTITUTION">기관</option>
          </select>
        )}
        <input type="text" className="form-control form-control-sm" style={{ ...inputStyle, width: 160 }}
          placeholder="종목코드/종목명"
          value={keywordInput}
          onChange={e => setKeywordInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSearchClick(); }} />
        <button className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1" onClick={handleSearchClick}>
          <IconSearch size={14} /> 조회
        </button>
        <select className="form-select form-select-sm ms-auto" style={{ ...inputStyle, width: 90 }}
          value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
          {[20, 50, 100, 200].map(s => <option key={s} value={s}>{s}건</option>)}
        </select>
      </div>

      {/* 탭 */}
      <ul className="nav nav-tabs mb-3" style={{ border: 'none', gap: 6 }}>
        {TABS.map(t => (
          <li className="nav-item" key={t.key}>
            <button
              className="nav-link"
              style={{
                background: activeTab === t.key ? '#206bc4' : '#1a2744',
                color: activeTab === t.key ? '#fff' : '#8fa1c7',
                border: '1px solid ' + (activeTab === t.key ? '#206bc4' : '#2a3a5a'),
                borderRadius: 6,
                fontWeight: activeTab === t.key ? 700 : 500,
                padding: '8px 18px',
                boxShadow: activeTab === t.key ? '0 2px 6px rgba(32,107,196,.4)' : 'none',
              }}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          </li>
        ))}
      </ul>

      <div style={{ background: '#152238', border: '1px solid #2a3a5a', borderRadius: 8, padding: 12, overflowX: 'auto' }}>
        {loading && <div style={{ color: '#7c8db0', fontSize: 12, padding: '8px 0' }}>불러오는 중...</div>}
        {!loading && (!data || !data.content || data.content.length === 0) && (
          <div style={{ color: '#7c8db0', fontSize: 12, padding: '8px 0' }}>조회된 데이터가 없습니다.</div>
        )}
        {!loading && data && data.content && data.content.length > 0 && (
          activeTab === 'stock' ? <StockTable rows={data.content} onClickStock={openDetail} favoriteStockCodes={favoriteStockCodes} onToggleFavorite={toggleStockFavorite} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} /> :
          activeTab === 'investor' ? <InvestorTable rows={data.content} onClickStock={openDetail} favoriteStockCodes={favoriteStockCodes} onToggleFavorite={toggleStockFavorite} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} /> :
          activeTab === 'valuation' ? <ValuationTable rows={data.content} onClickStock={openDetail} favoriteStockCodes={favoriteStockCodes} onToggleFavorite={toggleStockFavorite} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} /> :
          activeTab === 'short' ? <ShortSellingTable rows={data.content} onClickStock={openDetail} favoriteStockCodes={favoriteStockCodes} onToggleFavorite={toggleStockFavorite} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} /> :
          <EtfTable rows={data.content} onClickEtf={openDetail} favoriteStockCodes={favoriteStockCodes} onToggleFavorite={toggleStockFavorite} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
        )}
      </div>

      {data && data.totalPages > 0 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div style={{ color: '#7c8db0', fontSize: 12 }}>
            전체 {fmt(data.totalElements)}건 / {data.totalPages}페이지 중 {page + 1}페이지
          </div>
          <ul className="pagination pagination-sm m-0">
            <li className={`page-item ${page === 0 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setPage(p => Math.max(0, p - 1))}>이전</button>
            </li>
            {Array.from({ length: data.totalPages }, (_, i) => i)
              .filter(i => Math.abs(i - page) <= 3 || i === 0 || i === data.totalPages - 1)
              .reduce((acc, i, idx, arr) => {
                if (idx > 0 && i - arr[idx - 1] > 1) acc.push('gap');
                acc.push(i);
                return acc;
              }, [])
              .map((i, idx) => i === 'gap' ? (
                <li key={`gap-${idx}`} className="page-item disabled"><span className="page-link">…</span></li>
              ) : (
                <li key={i} className={`page-item ${page === i ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => setPage(i)}>{i + 1}</button>
                </li>
              ))}
            <li className={`page-item ${page >= data.totalPages - 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setPage(p => Math.min(data.totalPages - 1, p + 1))}>다음</button>
            </li>
          </ul>
        </div>
      )}

      {detailStock && (
        detailStock.isEtf ? (
          <KrxDataDetailModal
            stock={detailStock}
            onClose={() => setDetailStock(null)}
            isFavorite={favoriteStockCodes.has(detailStock.stockCode)}
            onToggleFavorite={() => toggleStockFavorite(detailStock.stockCode)}
          />
        ) : (
          <ScreeningDetailModal stock={detailStock} onClose={() => setDetailStock(null)} />
        )
      )}
    </div>
  );
}

/* ── 종목코드/종목명 클릭 링크 공통 ── */
function StockLink({ row, onClick }) {
  return (
    <button
      onClick={() => onClick(row)}
      style={{ background: 'none', border: 'none', padding: 0, color: '#7dabff', cursor: 'pointer', textAlign: 'left', font: 'inherit' }}
    >
      {row.stockName} <span style={{ color: '#7c8db0' }}>({row.stockCode})</span>
    </button>
  );
}

const th = { padding: '6px 8px', color: '#7c8db0', fontSize: 11, fontWeight: 600, textAlign: 'right', whiteSpace: 'nowrap', borderBottom: '1px solid #2a3a5a' };
const thL = { ...th, textAlign: 'left' };
const td = { padding: '5px 8px', textAlign: 'right', color: '#e6e6e6', fontSize: 12, whiteSpace: 'nowrap' };
const tdL = { ...td, textAlign: 'left' };

/* 헤더 클릭 정렬 공통 컴포넌트 */
function SortableTh({ label, field, sortBy, sortDir, onSort, align }) {
  const active = sortBy === field;
  return (
    <th
      style={{ ...(align === 'right' ? th : thL), cursor: 'pointer', userSelect: 'none', color: active ? '#7dabff' : (align === 'right' ? th.color : thL.color) }}
      onClick={() => onSort(field)}
    >
      {label}{active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );
}

/* 거래일 앞 즐겨찾기 별표 토글 */
function FavoriteStar({ isFavorite, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{ background: 'none', border: 'none', padding: 0, marginRight: 4, cursor: 'pointer', verticalAlign: 'middle', color: isFavorite ? '#f59f00' : '#4a5a7a' }}
      title={isFavorite ? '즐겨찾기 해제' : '즐겨찾기 추가'}
    >
      {isFavorite ? <IconStarFilled size={13} /> : <IconStar size={13} />}
    </button>
  );
}

function StockTable({ rows, onClickStock, favoriteStockCodes, onToggleFavorite, sortBy, sortDir, onSort }) {
  return (
    <table className="table table-sm mb-0" style={{ color: '#e6e6e6' }}>
      <thead>
        <tr>
          <SortableTh label="거래일" field="tradeDate" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <SortableTh label="시장" field="market" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <SortableTh label="종목" field="stockName" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <SortableTh label="ISIN" field="isinCode" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <SortableTh label="시가" field="openPrice" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="고가" field="highPrice" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="저가" field="lowPrice" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="종가" field="closePrice" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="거래량" field="tradingVolume" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="거래대금" field="tradingValue" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="시가총액" field="marketCap" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="상장주식수" field="listedShares" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="수집시간" field="collectedAt" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={`${r.stockCode}-${i}`}>
            <td style={tdL}><FavoriteStar isFavorite={favoriteStockCodes.has(r.stockCode)} onToggle={() => onToggleFavorite(r.stockCode)} />{r.tradeDate}</td>
            <td style={tdL}>{r.market}</td>
            <td style={tdL}><StockLink row={r} onClick={onClickStock} /></td>
            <td style={tdL}>{r.isinCode || '-'}</td>
            <td style={td}>{fmtWon(r.openPrice)}</td>
            <td style={td}>{fmtWon(r.highPrice)}</td>
            <td style={td}>{fmtWon(r.lowPrice)}</td>
            <td style={td}>{fmtWon(r.closePrice)}</td>
            <td style={td}>{fmt(r.tradingVolume)}</td>
            <td style={td}>{fmtWonCap(r.tradingValue)}</td>
            <td style={td}>{fmtWonCap(r.marketCap)}</td>
            <td style={td}>{fmt(r.listedShares)}</td>
            <td style={tdL}>{fmtDt(r.collectedAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const INVESTOR_LABELS = { INDIVIDUAL: '개인', FOREIGNER: '외국인', INSTITUTION: '기관' };

function InvestorTable({ rows, onClickStock, favoriteStockCodes, onToggleFavorite, sortBy, sortDir, onSort }) {
  return (
    <table className="table table-sm mb-0" style={{ color: '#e6e6e6' }}>
      <thead>
        <tr>
          <SortableTh label="거래일" field="tradeDate" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <SortableTh label="시장" field="market" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <SortableTh label="종목" field="stockName" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <SortableTh label="ISIN" field="isinCode" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <SortableTh label="투자자구분" field="investorType" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <SortableTh label="순매수거래량" field="netBuyVolume" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="순매수거래대금" field="netBuyValue" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="수집시간" field="collectedAt" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={`${r.stockCode}-${r.investorType}-${i}`}>
            <td style={tdL}><FavoriteStar isFavorite={favoriteStockCodes.has(r.stockCode)} onToggle={() => onToggleFavorite(r.stockCode)} />{r.tradeDate}</td>
            <td style={tdL}>{r.market}</td>
            <td style={tdL}><StockLink row={r} onClick={onClickStock} /></td>
            <td style={tdL}>{r.isinCode || '-'}</td>
            <td style={tdL}>{r.investorTypeLabel || INVESTOR_LABELS[r.investorType] || r.investorType}</td>
            <td style={td}>{fmt(r.netBuyVolume)}</td>
            <td style={td}>{fmtWonCap(r.netBuyValue)}</td>
            <td style={tdL}>{fmtDt(r.collectedAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ValuationTable({ rows, onClickStock, favoriteStockCodes, onToggleFavorite, sortBy, sortDir, onSort }) {
  return (
    <table className="table table-sm mb-0" style={{ color: '#e6e6e6' }}>
      <thead>
        <tr>
          <SortableTh label="거래일" field="tradeDate" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <SortableTh label="시장" field="market" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <SortableTh label="종목" field="stockName" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <SortableTh label="ISIN" field="isinCode" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <SortableTh label="EPS" field="eps" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="PER" field="per" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="BPS" field="bps" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="PBR" field="pbr" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="DPS" field="dps" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="배당수익률" field="dividendYield" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="수집시간" field="collectedAt" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={`${r.stockCode}-${i}`}>
            <td style={tdL}><FavoriteStar isFavorite={favoriteStockCodes.has(r.stockCode)} onToggle={() => onToggleFavorite(r.stockCode)} />{r.tradeDate}</td>
            <td style={tdL}>{r.market}</td>
            <td style={tdL}><StockLink row={r} onClick={onClickStock} /></td>
            <td style={tdL}>{r.isinCode || '-'}</td>
            <td style={td}>{fmtWon(r.eps)}</td>
            <td style={td}>{fmtDec2(r.per)}</td>
            <td style={td}>{fmtWon(r.bps)}</td>
            <td style={td}>{fmtDec2(r.pbr)}</td>
            <td style={td}>{fmtWon(r.dps)}</td>
            <td style={td}>{fmtPct(r.dividendYield)}</td>
            <td style={tdL}>{fmtDt(r.collectedAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ShortSellingTable({ rows, onClickStock, favoriteStockCodes, onToggleFavorite, sortBy, sortDir, onSort }) {
  return (
    <table className="table table-sm mb-0" style={{ color: '#e6e6e6' }}>
      <thead>
        <tr>
          <SortableTh label="거래일" field="tradeDate" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <SortableTh label="시장" field="market" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <SortableTh label="종목" field="stockName" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <SortableTh label="ISIN" field="isinCode" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <SortableTh label="공매도거래량" field="shortVolume" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="전체거래량" field="totalVolume" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="공매도비중" field="shortVolumeRatio" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="공매도거래대금" field="shortValue" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="공매도잔고" field="shortBalanceQuantity" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="공매도잔고비중" field="shortBalanceRatio" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="수집시간" field="collectedAt" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={`${r.stockCode}-${i}`}>
            <td style={tdL}><FavoriteStar isFavorite={favoriteStockCodes.has(r.stockCode)} onToggle={() => onToggleFavorite(r.stockCode)} />{r.tradeDate}</td>
            <td style={tdL}>{r.market}</td>
            <td style={tdL}><StockLink row={r} onClick={onClickStock} /></td>
            <td style={tdL}>{r.isinCode || '-'}</td>
            <td style={td}>{fmt(r.shortVolume)}</td>
            <td style={td}>{fmt(r.totalVolume)}</td>
            <td style={td}>{fmtPct(r.shortVolumeRatio)}</td>
            <td style={td}>{fmtWonCap(r.shortValue)}</td>
            <td style={td}>{fmt(r.shortBalanceQuantity)}</td>
            <td style={td}>{fmtPct(r.shortBalanceRatio)}</td>
            <td style={tdL}>{fmtDt(r.collectedAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ── ETF 탭 (krx_etf_daily 조회 API 사용) ── */
function EtfTable({ rows, onClickEtf, favoriteStockCodes, onToggleFavorite, sortBy, sortDir, onSort }) {
  return (
    <table className="table table-sm mb-0" style={{ color: '#e6e6e6' }}>
      <thead>
        <tr>
          <SortableTh label="거래일" field="tradeDate" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <SortableTh label="ETF" field="etfName" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <SortableTh label="ISIN" field="isinCode" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <SortableTh label="종가" field="closePrice" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="등락률" field="fluctuationRate" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="거래량" field="tradingVolume" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="거래대금" field="tradingValue" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="시가총액" field="marketCap" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="NAV" field="nav" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="수집시간" field="collectedAt" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={`${r.etfCode}-${i}`}>
            <td style={tdL}><FavoriteStar isFavorite={favoriteStockCodes.has(r.etfCode)} onToggle={() => onToggleFavorite(r.etfCode)} />{r.tradeDate}</td>
            <td style={tdL}>
              <button
                onClick={() => onClickEtf(r)}
                style={{ background: 'none', border: 'none', padding: 0, color: '#7dabff', cursor: 'pointer', textAlign: 'left', font: 'inherit' }}
              >
                {r.etfName} <span style={{ color: '#7c8db0' }}>({r.etfCode})</span>
              </button>
            </td>
            <td style={tdL}>{r.isinCode || '-'}</td>
            <td style={td}>{fmtWon(r.closePrice)}</td>
            <td style={td}>{fmtPct(r.fluctuationRate)}</td>
            <td style={td}>{fmt(r.tradingVolume)}</td>
            <td style={td}>{fmtWonCap(r.tradingValue)}</td>
            <td style={td}>{fmtWonCap(r.marketCap)}</td>
            <td style={td}>{fmtDec2(r.nav)}</td>
            <td style={tdL}>{fmtDt(r.collectedAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ── 종목 클릭 시 상세 레이어 (자산>시장분석 조건검색의 상세 레이어와 동일 API/패턴 재사용, ETF는 krx/etf/{code} 상세 API 사용) ── */
export function KrxDataDetailModal({ stock, onClose, isFavorite: isFavoriteProp, onToggleFavorite: onToggleFavoriteProp }) {
  const isMobile = useIsMobile();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  /* 즐겨찾기 상태를 외부(목록 페이지)에서 관리하지 않는 호출부(예: 주식장부)를 위해, props가 없으면 자체적으로 조회/토글한다 */
  const externallyManaged = onToggleFavoriteProp != null;
  const [selfFavorite, setSelfFavorite] = useState(false);
  const [favLoading, setFavLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const fetcher = stock.isEtf
      ? dataApi.getEtfDetail(stock.stockCode, stock.date)
      : dataApi.getScreeningDetail(stock.date, stock.market, stock.stockCode);
    fetcher
      .then(setDetail)
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [stock]);

  useEffect(() => {
    if (externallyManaged) return;
    dataApi.getFavorites()
      .then(list => setSelfFavorite((list || []).includes(stock.stockCode)))
      .catch(() => setSelfFavorite(false));
  }, [stock, externallyManaged]);

  const isFavorite = externallyManaged ? isFavoriteProp : selfFavorite;
  const toggleFavorite = async () => {
    if (externallyManaged) { onToggleFavoriteProp(); return; }
    setFavLoading(true);
    try {
      if (selfFavorite) await dataApi.removeFavorite(stock.stockCode); else await dataApi.addFavorite(stock.stockCode);
      setSelfFavorite(v => !v);
    } catch (e) {
      alert(`즐겨찾기 처리 실패: ${e.response?.data?.message || e.message}`);
    } finally {
      setFavLoading(false);
    }
  };

  const row = (label, value) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #1e2a44' }}>
      <span style={{ color: '#7c8db0', fontSize: 11 }}>{label}</span>
      <span style={{ color: '#e6e6e6', fontSize: 12, fontWeight: 600 }}>{value}</span>
    </div>
  );

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: '#152238', border: '1px solid #2a3a5a', borderRadius: 8, padding: isMobile ? 10 : 16, maxWidth: '92vw', width: isMobile ? '100%' : 680, maxHeight: '90vh', overflow: 'auto' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
          <div style={{ color: '#e6e6e6', fontSize: isMobile ? 12 : 14, fontWeight: 700 }}>
            {!stock.isEtf && <span style={{ color: '#7c8db0', fontSize: 11, marginRight: 6 }}>{stock.market === 'KOSPI' ? '코스피' : '코스닥'}</span>}
            {stock.stockName} ({stock.stockCode})
            <span style={{ opacity: !externallyManaged && favLoading ? 0.5 : 1, pointerEvents: !externallyManaged && favLoading ? 'none' : 'auto' }}>
              <FavoriteStar isFavorite={isFavorite} onToggle={toggleFavorite} />
            </span>
          </div>
          <div className="d-flex gap-2">
            <a className="btn btn-sm btn-outline-success" href={`https://finance.naver.com/item/main.naver?code=${stock.stockCode}`}
              target="_blank" rel="noopener noreferrer">네이버증권</a>
            <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>닫기</button>
          </div>
        </div>

        {loading && <div style={{ color: '#7c8db0', fontSize: 12 }}>불러오는 중...</div>}
        {!loading && !detail && <div style={{ color: '#7c8db0', fontSize: 12 }}>상세 데이터를 불러오지 못했습니다.</div>}

        {!loading && detail && stock.isEtf && (
          <div className="d-flex gap-4 flex-wrap">
            <div style={{ flex: '1 1 280px', minWidth: 240 }}>
              <div style={{ color: '#7c8db0', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>기본정보</div>
              {row('기준일', detail.tradeDate)}
              {row('ISIN', detail.isinCode || '-')}
              {row('종가', fmtWon(detail.closePrice))}
              {row('시가', fmtWon(detail.openPrice))}
              {row('고가', fmtWon(detail.highPrice))}
              {row('저가', fmtWon(detail.lowPrice))}
              {row('등락률', fmtPct(detail.fluctuationRate))}
            </div>
            <div style={{ flex: '1 1 280px', minWidth: 240 }}>
              <div style={{ color: '#7c8db0', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>거래/지수 정보</div>
              {row('거래량', fmt(detail.tradingVolume))}
              {row('거래대금', fmtWonCap(detail.tradingValue))}
              {row('시가총액', fmtWonCap(detail.marketCap))}
              {row('상장좌수', fmt(detail.listedShares))}
              {row('NAV', detail.nav == null ? '-' : Number(detail.nav).toFixed(4))}
              {row('기초지수명', detail.baseIndexName || '-')}
              {row('기초지수값', fmtDec2(detail.baseIndexValue))}
              {row('수집시간', fmtDt(detail.collectedAt))}
            </div>
          </div>
        )}

        {!loading && detail && !stock.isEtf && (
          <div className="d-flex gap-4 flex-wrap">
            <div style={{ flex: '1 1 280px', minWidth: 240 }}>
              <div style={{ color: '#7c8db0', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>기본정보</div>
              {row('기준일', detail.tradeDate)}
              {row('현재가', fmtWon(detail.closePrice))}
              {row('시가총액', fmtWonCap(detail.marketCap))}
              {row('거래량', fmt(detail.tradingVolume))}

              <div style={{ color: '#7c8db0', fontSize: 11, fontWeight: 700, margin: '10px 0 4px' }}>밸류에이션</div>
              {row('PBR', fmtDec2(detail.pbr))}
              {row('PER', fmtDec2(detail.per))}
              {row('EPS', fmtWon(detail.eps))}
              {row('BPS', fmtWon(detail.bps))}
              {row('DPS', fmtWon(detail.dps))}
              {row('배당수익률', fmtPct(detail.dividendYield))}
            </div>

            <div style={{ flex: '1 1 280px', minWidth: 240 }}>
              <div style={{ color: '#7c8db0', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>투자자 수급 (기준일 스냅샷)</div>
              {row('개인 순매수', fmtWonCap(detail.individualNetBuyValue))}
              {row('외국인 순매수', fmtWonCap(detail.foreignNetBuyValue))}
              {row('기관 순매수', fmtWonCap(detail.institutionNetBuyValue))}

              <div style={{ color: '#7c8db0', fontSize: 11, fontWeight: 700, margin: '10px 0 4px' }}>공매도 (기준일 스냅샷)</div>
              {row('공매도 거래량', fmt(detail.shortVolume))}
              {row('총 거래량', fmt(detail.totalVolume))}
              {row('거래비중', fmtPct(detail.shortVolumeRatio))}
              {row('잔고수량', fmt(detail.shortBalanceQuantity))}
              {row('잔고금액', fmtWonCap(detail.shortBalanceAmount))}
              {row('잔고비중', fmtPct(detail.shortBalanceRatio))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
